import { CheckCircle2, LoaderCircle, Undo2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { auditAuthorLabel, auditAuthorTitle } from "@/features/admin/audit-author";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { apiClient, type BulkEdit } from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { Pager } from "@/shared/components/Pager";
import { usePolling } from "@/shared/hooks/usePolling";
import {
  bulkStatusLabel,
  bulkStatusTone,
  describeBulkPatch,
  failureLines,
  isBulkRunning,
  undoOutcomeMessage
} from "./bulk-edit-history";

const PAGE_SIZE = 25;

type Notice = { message: string; lines: string[] };

/**
 * A aba "Lotes" do histórico de edições (2026-10-22): cada edição em lote
 * da empresa, com o que ela mudou, o desfecho e o "Desfazer".
 *
 * Quem decide se dá pra desfazer é o SERVIDOR (`can_undo`); quando não dá,
 * a razão (`cannot_undo_reason`) fica à vista em vez de o botão sumir sem
 * explicação. Desfazer até 100 menções volta `FINISHED` na própria
 * resposta; acima disso vai pra fila e esta aba acompanha o lote novo por
 * polling (que pausa no 429) até terminar. Falha de AÇÃO (409, 404, 429)
 * vira mensagem aqui, nunca troca a lista.
 */
export function BulkEditsTab({
  token,
  onShowChanges,
  onUndoFinished
}: {
  token: string;
  /** "Ver alterações" de um lote: leva pra aba de alterações filtrada. */
  onShowChanges: (bulkEditId: number) => void;
  /** Um desfazer terminou: a aba de alterações ganhou linhas novas. */
  onUndoFinished?: () => void;
}) {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<BulkEdit[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState<BulkEdit | null>(null);
  const [undoing, setUndoing] = useState(false);
  // O lote de desfazer que ficou na fila e está sendo acompanhado.
  const [tracking, setTracking] = useState<BulkEdit | null>(null);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Descarte por CARGA: a resposta de uma consulta antiga não vence a atual.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const result = await apiClient.listBulkEditHistory(token, { page, size: PAGE_SIZE });
      if (seq !== loadSeq.current) return;
      setItems(result.items);
      setTotal(result.total);
      setError(null);
    } catch (failure) {
      if (seq !== loadSeq.current) return;
      setError(errorMessage(failure, "Não foi possível carregar os lotes."));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    void load();
  }, [load]);

  /** O desfazer terminou: diz o desfecho (com os motivos de quem ficou de
   *  fora, quando houve) e recarrega. A recarga vem ANTES do aviso: uma
   *  carga que dá certo não pode apagar o que acabou de ser dito. */
  const finishUndo = useCallback(
    async (result: BulkEdit) => {
      setTracking(null);
      let lines: string[] = [];
      if (result.counters.failed > 0) {
        try {
          lines = failureLines(await apiClient.listBulkEditFailures(token, result.id));
        } catch {
          // Sem os motivos o aviso ainda diz quantas ficaram de fora.
        }
      }
      await load();
      setNotice(
        result.status === "ERRORED"
          ? { message: `O desfazer parou com erro${result.message ? `: ${result.message}` : "."}`, lines }
          : { message: undoOutcomeMessage(result), lines }
      );
      onUndoFinished?.();
    },
    [token, load, onUndoFinished]
  );

  const confirmUndo = async () => {
    if (!confirming) return;
    const target = confirming;
    setUndoing(true);
    setActionError(null);
    setNotice(null);
    try {
      const result = await apiClient.undoBulkEdit(token, target.id);
      setConfirming(null);
      if (isBulkRunning(result)) {
        setTracking(result);
        void load();
      } else {
        await finishUndo(result);
      }
    } catch (failure) {
      setConfirming(null);
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
      setActionError(errorMessage(failure, "Não foi possível desfazer o lote."));
      // O estado do lote pode ter mudado (já desfeito, ainda rodando): a
      // lista se refaz pra mostrar o que o servidor sabe hoje.
      void load();
    } finally {
      setUndoing(false);
    }
  };

  const refreshTracked = useCallback(async () => {
    if (!tracking) return;
    try {
      const next = await apiClient.getBulkEdit(token, tracking.id);
      if (isBulkRunning(next)) {
        setTracking(next);
      } else {
        await finishUndo(next);
      }
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
    }
  }, [token, tracking, finishUndo]);

  usePolling({
    enabled: tracking !== null,
    onTick: () => void refreshTracked(),
    signature: tracking ? `${tracking.id}:${tracking.status}:${tracking.counters.pending}` : undefined,
    pausedUntil
  });

  const busy = undoing || tracking !== null;

  return (
    <>
      {tracking && (
        <p className="edit-history-notice" role="status" aria-live="polite">
          <LoaderCircle className="spin" size={16} aria-hidden="true" />
          Desfazendo o lote #{tracking.undo_of_id ?? tracking.patch.undo_of}…{" "}
          {(tracking.counters.total - tracking.counters.pending).toLocaleString("pt-BR")} de{" "}
          {tracking.counters.total.toLocaleString("pt-BR")}
        </p>
      )}
      {notice && (
        <div className="edit-history-notice is-done" role="status" aria-live="polite">
          <p>
            <CheckCircle2 size={16} aria-hidden="true" />
            {notice.message}
            <button
              type="button"
              className="icon-button"
              onClick={() => setNotice(null)}
              aria-label="Fechar aviso"
            >
              <X size={14} />
            </button>
          </p>
          {notice.lines.length > 0 && (
            <ul className="bulk-failures">
              {notice.lines.map(line => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && (
        <ApiState kind="error" onRetry={() => void load()}>
          <span role="alert">{error}</span>
        </ApiState>
      )}
      {actionError && (
        <p className="form-error" role="alert">
          {actionError}
        </p>
      )}

      <div className="edit-history-list">
        {items !== null && items.length === 0 ? (
          <p className="copiloto-empty-list">Nenhuma edição em lote registrada.</p>
        ) : (
          items !== null && (
            <table className="ai-admin-table audit-table">
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Pedido</th>
                  <th>O que mudou</th>
                  <th>Resultado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map(edit => (
                  <BatchRow
                    key={edit.id}
                    edit={edit}
                    busy={busy}
                    onUndo={() => {
                      setActionError(null);
                      setConfirming(edit);
                    }}
                    onShowChanges={() => onShowChanges(edit.id)}
                  />
                ))}
              </tbody>
            </table>
          )
        )}

        {items === null && !error && <ApiState kind="loading">Carregando…</ApiState>}
        {loading && items !== null && <LoaderCircle className="spin" size={16} aria-label="Atualizando" />}

        {total !== null && (
          <Pager page={page} size={PAGE_SIZE} count={{ total }} onPage={setPage} />
        )}
      </div>

      {confirming && (
        <ConfirmDialog
          title={`Desfazer o lote #${confirming.id}?`}
          description={`${confirming.counters.changed.toLocaleString("pt-BR")} ${
            confirming.counters.changed === 1 ? "menção volta" : "menções voltam"
          } ao valor anterior. Menção alterada depois do lote fica de fora, e o desfazer não pode ser desfeito.`}
          confirmLabel="Desfazer"
          danger
          loading={undoing}
          onConfirm={() => void confirmUndo()}
          onCancel={() => {
            if (!undoing) setConfirming(null);
          }}
        />
      )}
    </>
  );
}

function BatchRow({
  edit,
  busy,
  onUndo,
  onShowChanges
}: {
  edit: BulkEdit;
  busy: boolean;
  onUndo: () => void;
  onShowChanges: () => void;
}) {
  const when = parseServerTime(edit.requested_at);
  const { counters } = edit;
  const lines = describeBulkPatch(edit.patch);
  const running = isBulkRunning(edit);
  const tone = bulkStatusTone(edit.status);
  return (
    <tr>
      <td>
        <strong>Lote #{edit.id}</strong>
        <br />
        <span className={`upload-status ${tone}`}>
          {running && <LoaderCircle className="spin" size={12} aria-hidden="true" />}
          {bulkStatusLabel(edit.status)}
        </span>
      </td>
      <td title={auditAuthorTitle({ changed_by: edit.requested_by, changed_by_name: edit.requested_by_name })}>
        {auditAuthorLabel({ changed_by: edit.requested_by, changed_by_name: edit.requested_by_name })}
        <br />
        <small>{when === null ? edit.requested_at : new Date(when).toLocaleString("pt-BR")}</small>
      </td>
      <td className="audit-fields">
        {lines.map(line => (
          <div key={line}>{line}</div>
        ))}
      </td>
      <td>
        {running ? (
          <>
            {(counters.total - counters.pending).toLocaleString("pt-BR")} de{" "}
            {counters.total.toLocaleString("pt-BR")}
          </>
        ) : (
          <>
            {counters.changed.toLocaleString("pt-BR")} alterada{counters.changed === 1 ? "" : "s"}
            {counters.unchanged > 0 && <> · {counters.unchanged.toLocaleString("pt-BR")} sem mudança</>}
            {counters.failed > 0 && <> · {counters.failed.toLocaleString("pt-BR")} com falha</>}
          </>
        )}
        {edit.undone_by_id != null && (
          <>
            <br />
            <span className="report-criteria-chip">Desfeito pelo lote #{edit.undone_by_id}</span>
          </>
        )}
      </td>
      <td className="edit-history-batch-actions">
        {edit.can_undo && (
          <button type="button" className="secondary-button" onClick={onUndo} disabled={busy}>
            <Undo2 size={14} aria-hidden="true" />
            Desfazer
          </button>
        )}
        <button type="button" className="text-button" onClick={onShowChanges}>
          Ver alterações
        </button>
        {!edit.can_undo && edit.cannot_undo_reason && (
          <small className="edit-history-reason">{edit.cannot_undo_reason}</small>
        )}
      </td>
    </tr>
  );
}
